import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ordersAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  MapPinIcon,
  ArrowRightIcon,
  PhoneIcon,
  CameraIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import RiderRatingModal from './RiderRatingModal';
import api from '../../services/api';

// Status workflow configuration
const STATUS_WORKFLOW = [
  { 
    key: 'assigned', 
    label: 'Job Accepted', 
    color: 'bg-blue-100 text-blue-800',
    action: null
  },
  { 
    key: 'en_route_to_pickup', 
    label: 'On My Way to Pickup', 
    color: 'bg-indigo-100 text-indigo-800',
    action: { status: 'en_route_to_pickup', label: 'Start Trip to Pickup' }
  },
  { 
    key: 'at_pickup', 
    label: 'At Pickup Location', 
    color: 'bg-purple-100 text-purple-800',
    action: { status: 'at_pickup', label: 'Arrived at Pickup' }
  },
  { 
    key: 'picked_up', 
    label: 'Package Picked Up', 
    color: 'bg-volexx-orange-100 text-volexx-orange-800',
    action: { status: 'picked_up', label: 'Confirm Pickup' }
  },
  { 
    key: 'en_route_to_delivery', 
    label: 'On My Way to Delivery', 
    color: 'bg-yellow-100 text-yellow-800',
    action: { status: 'en_route_to_delivery', label: 'Start Trip to Delivery' }
  },
  { 
    key: 'at_delivery', 
    label: 'At Delivery Location', 
    color: 'bg-lime-100 text-lime-800',
    action: { status: 'at_delivery', label: 'Arrived at Delivery' }
  },
  { 
    key: 'delivered', 
    label: 'Delivered', 
    color: 'bg-green-100 text-green-800',
    action: null // OTP verification required
  }
];

const MyJobs: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});
  const [otps, setOtps] = useState<{ [key: string]: string }>({});
  const [uploadingPhoto, setUploadingPhoto] = useState<{ [key: string]: boolean }>({});
  const [pickupPhotos, setPickupPhotos] = useState<{ [key: string]: string }>({});
  const [deliveryPhotos, setDeliveryPhotos] = useState<{ [key: string]: string }>({});
  const [pickupChecklist, setPickupChecklist] = useState<{ [key: string]: { otpMatch: boolean; senderNameMatch: boolean; phoneNumberMatch: boolean; packageConfirmed: boolean } }>({});
  const [deliveryChecklist, setDeliveryChecklist] = useState<{ [key: string]: { otpMatch: boolean; receiverNameMatch: boolean; phoneNumberMatch: boolean; packageCondition: boolean } }>({});
  const pickupFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const deliveryFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [showRatingModal, setShowRatingModal] = useState<string | null>(null);
  const [ratedOrders, setRatedOrders] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();

  // Memoized fetch function to prevent recreating on every render
  const fetchMyJobs = useCallback(async () => {
    try {
      setLoading(true);
      const allOrders = await ordersAPI.getUserOrders();
      
      // Filter to show only jobs assigned to the current dispatcher
      // Backend already filters by rider_id, but this adds an extra safety check
      const myActiveJobs = allOrders.filter((order: any) => {
        // Only show orders that are:
        // 1. Assigned to this specific dispatcher (rider_uuid matches current user UUID)
        // 2. Not in pending or cancelled status
        const isAssignedToMe = order.rider_uuid === user?.id;
        const isActiveStatus = order.status !== 'pending' && order.status !== 'cancelled';
        
        return isAssignedToMe && isActiveStatus;
      });
      
      setOrders(myActiveJobs);
      
      // Fetch OTPs for all jobs
      const otpPromises = myActiveJobs.map((order: any) => 
        ordersAPI.getOrderOTP(order.id).catch(() => null)
      );
      const otpResults = await Promise.all(otpPromises);
      
      const otpMap: { [key: string]: string } = {};
      const ratingStatusMap: { [key: string]: boolean } = {};
      
      myActiveJobs.forEach((order: any, index: number) => {
        if (otpResults[index]) {
          otpMap[order.id] = otpResults[index].otp_code;
        }
      });
      setOtps(otpMap);
      
      // Check rating status for delivered orders
      const deliveredOrders = myActiveJobs.filter((order: any) => order.status === 'delivered');
      const ratingCheckPromises = deliveredOrders.map((order: any) =>
        api.get(`/order/${order.id}/my-rating`)
          .then(res => ({ orderId: order.id, hasRated: res.data.has_rated }))
          .catch(() => ({ orderId: order.id, hasRated: false }))
      );
      
      const ratingResults = await Promise.all(ratingCheckPromises);
      ratingResults.forEach(result => {
        if (result.hasRated) {
          ratingStatusMap[result.orderId] = true;
        }
      });
      
      // Merge with previous state to preserve already-rated orders
      setRatedOrders(prev => ({ ...prev, ...ratingStatusMap }));
    } catch (err: any) {
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Setup polling with proper cleanup
  useEffect(() => {
    fetchMyJobs();
    const interval = setInterval(fetchMyJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchMyJobs]);

  const getCurrentStepIndex = (status: string): number => {
    const index = STATUS_WORKFLOW.findIndex(step => step.key === status);
    return index >= 0 ? index : 0;
  };

  const getNextAction = (currentStatus: string) => {
    const currentIndex = getCurrentStepIndex(currentStatus);
    if (currentIndex < STATUS_WORKFLOW.length - 1) {
      return STATUS_WORKFLOW[currentIndex + 1].action;
    }
    return null;
  };

  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
      await ordersAPI.updateOrderStatus(orderId, newStatus);
      await fetchMyJobs();
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handlePickupPhotoUpload = async (orderId: string, file: File) => {
    try {
      setUploadingPhoto(prev => ({ ...prev, [`pickup_${orderId}`]: true }));
      const result = await ordersAPI.uploadPickupPhoto(orderId, file);
      setPickupPhotos(prev => ({ ...prev, [orderId]: result.photo_url }));
      alert('Photo uploaded successfully! You can now confirm pickup.');
      await fetchMyJobs();
    } catch (err: any) {
      alert(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(prev => ({ ...prev, [`pickup_${orderId}`]: false }));
    }
  };

  const handleDeliveryPhotoUpload = async (orderId: string, file: File) => {
    try {
      setUploadingPhoto(prev => ({ ...prev, [`delivery_${orderId}`]: true }));
      const result = await ordersAPI.uploadDeliveryPhoto(orderId, file);
      setDeliveryPhotos(prev => ({ ...prev, [orderId]: result.photo_url }));
      alert('Delivery photo uploaded successfully! Package is ready to be confirmed as delivered.');
      await fetchMyJobs();
    } catch (err: any) {
      alert(err.message || 'Failed to upload delivery photo');
    } finally {
      setUploadingPhoto(prev => ({ ...prev, [`delivery_${orderId}`]: false }));
    }
  };

  const handlePickupFileSelect = (orderId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size must be less than 5MB');
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      handlePickupPhotoUpload(orderId, file);
    }
  };

  const handleDeliveryFileSelect = (orderId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size must be less than 5MB');
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      handleDeliveryPhotoUpload(orderId, file);
    }
  };

  const triggerPickupPhotoCapture = (orderId: string) => {
    pickupFileInputRefs.current[orderId]?.click();
  };

  const triggerDeliveryPhotoCapture = (orderId: string) => {
    deliveryFileInputRefs.current[orderId]?.click();
  };

  const viewOrderDetails = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const togglePickupChecklistItem = (orderId: string, item: 'otpMatch' | 'senderNameMatch' | 'phoneNumberMatch' | 'packageConfirmed') => {
    setPickupChecklist(prev => ({
      ...prev,
      [orderId]: {
        otpMatch: prev[orderId]?.otpMatch || false,
        senderNameMatch: prev[orderId]?.senderNameMatch || false,
        phoneNumberMatch: prev[orderId]?.phoneNumberMatch || false,
        packageConfirmed: prev[orderId]?.packageConfirmed || false,
        [item]: !prev[orderId]?.[item]
      }
    }));
  };

  const isPickupChecklistComplete = (orderId: string): boolean => {
    const checklist = pickupChecklist[orderId];
    if (!checklist) return false;
    return checklist.otpMatch && checklist.senderNameMatch && checklist.phoneNumberMatch && checklist.packageConfirmed;
  };

  const toggleDeliveryChecklistItem = (orderId: string, item: 'otpMatch' | 'receiverNameMatch' | 'phoneNumberMatch' | 'packageCondition') => {
    setDeliveryChecklist(prev => ({
      ...prev,
      [orderId]: {
        otpMatch: prev[orderId]?.otpMatch || false,
        receiverNameMatch: prev[orderId]?.receiverNameMatch || false,
        phoneNumberMatch: prev[orderId]?.phoneNumberMatch || false,
        packageCondition: prev[orderId]?.packageCondition || false,
        [item]: !prev[orderId]?.[item]
      }
    }));
  };

  const isDeliveryChecklistComplete = (orderId: string): boolean => {
    const checklist = deliveryChecklist[orderId];
    if (!checklist) return false;
    return checklist.otpMatch && checklist.receiverNameMatch && checklist.phoneNumberMatch && checklist.packageCondition;
  };

  const handleCompleteDelivery = async (orderId: string) => {
    if (!isDeliveryChecklistComplete(orderId)) {
      alert('Please complete all verification checks before marking as delivered');
      return;
    }
    await handleStatusUpdate(orderId, 'delivered');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">My Jobs</h1>
          <p className="mt-2 text-gray-600 font-poppins">Manage your active delivery jobs and track progress.</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-volexx font-poppins">
            {error}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="bg-white rounded-volexx shadow-volexx p-12 text-center">
            <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2 font-poppins">No active jobs</h3>
            <p className="text-gray-500 font-poppins">Jobs you accept will appear here. Visit Available Jobs to find new deliveries.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => {
              const currentStepIndex = getCurrentStepIndex(order.status);
              const nextAction = getNextAction(order.status);
              const isUpdating = updatingStatus[order.id];
              
              return (
                <div key={order.id} className="bg-white rounded-volexx shadow-volexx border border-gray-100 overflow-hidden">
                  {/* Order Header */}
                  <div className="bg-gradient-blue p-4">
                    <div className="flex items-center justify-between text-white">
                      <div>
                        <h3 className="text-lg font-bold font-poppins">Order #{String(order.id).slice(-8)}</h3>
                        <p className="text-sm opacity-90 font-poppins">
                          {order.vehicle_type} • {order.package_type}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-poppins">₦{Number(order.final_price || 0).toFixed(2)}</p>
                        <p className="text-xs opacity-90 font-poppins">Earnings: ₦{(Number(order.final_price || 0) * 0.8).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Route Information */}
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-volexx p-4">
                        <div className="flex items-start">
                          <MapPinIcon className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-green-800 mb-1 font-poppins">PICKUP LOCATION</p>
                            <p className="text-sm text-gray-900 font-poppins">{order.stops?.[0]?.address || 'N/A'}</p>
                            {order.stops?.[0]?.contact_name && (
                              <div className="mt-2 space-y-1">
                                <p className="text-xs text-gray-700 font-poppins flex items-center">
                                  <PhoneIcon className="h-3 w-3 mr-1" />
                                  {order.stops[0].contact_phone}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-50 border border-red-200 rounded-volexx p-4">
                        <div className="flex items-start">
                          <MapPinIcon className="h-5 w-5 text-red-600 mr-2 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-red-800 mb-1 font-poppins">DELIVERY LOCATION</p>
                            <p className="text-sm text-gray-900 font-poppins">{order.stops?.[1]?.address || 'N/A'}</p>
                            {order.stops?.[1]?.contact_name && (
                              <div className="mt-2 space-y-1">
                                <p className="text-xs text-gray-700 font-poppins flex items-center">
                                  <PhoneIcon className="h-3 w-3 mr-1" />
                                  {order.stops[1].contact_phone}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workflow Progress */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 font-poppins">Delivery Progress</h4>
                      <div className="relative">
                        {/* Progress Bar */}
                        <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full">
                          <div 
                            className="h-full bg-volexx-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${(currentStepIndex / (STATUS_WORKFLOW.length - 1)) * 100}%` }}
                          ></div>
                        </div>

                        {/* Status Steps */}
                        <div className="relative flex justify-between">
                          {STATUS_WORKFLOW.map((step, index) => {
                            const isCompleted = index < currentStepIndex;
                            const isCurrent = index === currentStepIndex;

                            return (
                              <div key={step.key} className="flex flex-col items-center" style={{ width: `${100 / STATUS_WORKFLOW.length}%` }}>
                                <div 
                                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 border-2 transition-all ${
                                    isCompleted 
                                      ? 'bg-volexx-blue-600 border-volexx-blue-600' 
                                      : isCurrent 
                                      ? 'bg-white border-volexx-blue-600 ring-4 ring-volexx-blue-100' 
                                      : 'bg-gray-100 border-gray-300'
                                  }`}
                                >
                                  {isCompleted ? (
                                    <CheckCircleIcon className="h-6 w-6 text-white" />
                                  ) : (
                                    <span className={`text-sm font-bold font-poppins ${isCurrent ? 'text-volexx-blue-600' : 'text-gray-400'}`}>
                                      {index + 1}
                                    </span>
                                  )}
                                </div>
                                <p className={`text-xs text-center font-poppins ${
                                  isCurrent ? 'font-semibold text-volexx-blue-900' : 'text-gray-600'
                                }`}>
                                  {step.label}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Current Status Badge */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-volexx-blue-50 to-volexx-orange-50 rounded-volexx border border-volexx-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 font-poppins mb-1">Current Status</p>
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-volexx ${STATUS_WORKFLOW[currentStepIndex].color} font-poppins`}>
                            {STATUS_WORKFLOW[currentStepIndex].label}
                          </span>
                        </div>
                        <ClockIcon className="h-8 w-8 text-volexx-blue-600" />
                      </div>
                    </div>

                    {/* Delivery OTP Display */}
                    {otps[order.id] && (
                      <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-volexx border-2 border-green-300">
                        <div className="text-center">
                          <p className="text-xs font-semibold text-green-800 mb-2 font-poppins uppercase">Delivery OTP</p>
                          <div className="bg-white rounded-volexx p-3 mb-2">
                            <p className="text-3xl font-bold text-green-700 font-mono tracking-widest">
                              {otps[order.id]}
                            </p>
                          </div>
                          <p className="text-xs text-gray-600 font-poppins">
                            Share this code with the receiver to complete delivery
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Pickup Verification Checklist - Show when at pickup location */}
                    {order.status === 'at_pickup' && !isPickupChecklistComplete(order.id) && (
                      <div className="mb-6 p-5 bg-gradient-to-r from-volexx-orange-50 to-amber-50 rounded-volexx border-2 border-volexx-orange-300">
                        <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins flex items-center">
                          <CheckCircleIcon className="h-6 w-6 text-volexx-orange-600 mr-2" />
                          Pickup Verification Checklist
                        </h4>
                        <p className="text-sm text-gray-600 mb-4 font-poppins">
                          Verify all details before capturing package photo
                        </p>
                        
                        <div className="space-y-3">
                          {/* OTP Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-volexx-orange-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={pickupChecklist[order.id]?.otpMatch || false}
                              onChange={() => togglePickupChecklistItem(order.id, 'otpMatch')}
                              className="mt-1 h-5 w-5 text-volexx-orange-600 rounded focus:ring-2 focus:ring-volexx-orange-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">OTP Code Confirmed</p>
                              <p className="text-xs text-gray-600 font-poppins">Sender verified with OTP: {otps[order.id]}</p>
                            </div>
                          </label>

                          {/* Sender Name Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-volexx-orange-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={pickupChecklist[order.id]?.senderNameMatch || false}
                              onChange={() => togglePickupChecklistItem(order.id, 'senderNameMatch')}
                              className="mt-1 h-5 w-5 text-volexx-orange-600 rounded focus:ring-2 focus:ring-volexx-orange-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Sender Name Verified</p>
                              <p className="text-xs text-gray-600 font-poppins">
                                Confirmed identity: {order.stops?.[0]?.contact_name || 'N/A'}
                              </p>
                            </div>
                          </label>

                          {/* Phone Number Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-volexx-orange-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={pickupChecklist[order.id]?.phoneNumberMatch || false}
                              onChange={() => togglePickupChecklistItem(order.id, 'phoneNumberMatch')}
                              className="mt-1 h-5 w-5 text-volexx-orange-600 rounded focus:ring-2 focus:ring-volexx-orange-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Phone Number Verified</p>
                              <p className="text-xs text-gray-600 font-poppins">
                                Confirmed contact: {order.stops?.[0]?.contact_phone || 'N/A'}
                              </p>
                            </div>
                          </label>

                          {/* Package Confirmed */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-volexx-orange-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={pickupChecklist[order.id]?.packageConfirmed || false}
                              onChange={() => togglePickupChecklistItem(order.id, 'packageConfirmed')}
                              className="mt-1 h-5 w-5 text-volexx-orange-600 rounded focus:ring-2 focus:ring-volexx-orange-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Package Details Confirmed</p>
                              <p className="text-xs text-gray-600 font-poppins">
                                Package matches description: {order.package_description || 'N/A'}
                              </p>
                            </div>
                          </label>
                        </div>
                        
                        {!isPickupChecklistComplete(order.id) && (
                          <p className="text-xs text-center text-gray-500 mt-4 font-poppins">
                            ✓ Complete all verification checks to proceed to photo capture
                          </p>
                        )}
                      </div>
                    )}

                    {/* Photo Capture Section - Show when checklist is complete */}
                    {order.status === 'at_pickup' && isPickupChecklistComplete(order.id) && (
                      <div className="mb-6">
                        {!order.pickup_photo_url && !pickupPhotos[order.id] ? (
                          <div className="p-6 bg-gradient-to-r from-volexx-orange-50 to-amber-50 rounded-volexx border-2 border-volexx-orange-300">
                            <div className="text-center">
                              <CameraIcon className="h-12 w-12 text-volexx-orange-600 mx-auto mb-3" />
                              <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">
                                Capture Package Photo
                              </h4>
                              <p className="text-sm text-gray-600 mb-4 font-poppins">
                                Take a clear photo of the package before confirming pickup
                              </p>
                              <input
                                type="file"
                                ref={(el) => { pickupFileInputRefs.current[order.id] = el; }}
                                onChange={(e) => handlePickupFileSelect(order.id, e)}
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                              />
                              <button
                                onClick={() => triggerPickupPhotoCapture(order.id)}
                                disabled={uploadingPhoto[`pickup_${order.id}`]}
                                className="bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-3 px-6 rounded-volexx transition-colors inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                              >
                                {uploadingPhoto[`pickup_${order.id}`] ? (
                                  <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <CameraIcon className="h-5 w-5 mr-2" />
                                    Take Photo
                                  </>
                                )}
                              </button>
                              <p className="text-xs text-gray-500 mt-3 font-poppins">
                                Photo is required before you can confirm pickup
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-green-50 rounded-volexx border-2 border-green-300">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                                <div>
                                  <p className="text-sm font-semibold text-green-800 font-poppins">
                                    Photo Uploaded Successfully
                                  </p>
                                  <p className="text-xs text-gray-600 font-poppins">
                                    You can now confirm pickup
                                  </p>
                                </div>
                              </div>
                              <img 
                                src={pickupPhotos[order.id] || order.pickup_photo_url} 
                                alt="Pickup package" 
                                className="h-16 w-16 object-cover rounded-volexx border-2 border-green-400"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Delivery Photo Capture Section - Show when at delivery location */}
                    {order.status === 'at_delivery' && (
                      <div className="mb-6">
                        {!order.delivery_photo_url && !deliveryPhotos[order.id] ? (
                          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-volexx border-2 border-green-300">
                            <div className="text-center">
                              <CameraIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
                              <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">
                                Capture Delivery Photo
                              </h4>
                              <p className="text-sm text-gray-600 mb-4 font-poppins">
                                Take a photo of the package with the receiver before confirming delivery
                              </p>
                              <input
                                type="file"
                                ref={(el) => { deliveryFileInputRefs.current[order.id] = el; }}
                                onChange={(e) => handleDeliveryFileSelect(order.id, e)}
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                              />
                              <button
                                onClick={() => triggerDeliveryPhotoCapture(order.id)}
                                disabled={uploadingPhoto[`delivery_${order.id}`]}
                                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-volexx transition-colors inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                              >
                                {uploadingPhoto[`delivery_${order.id}`] ? (
                                  <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <CameraIcon className="h-5 w-5 mr-2" />
                                    Take Photo with Receiver
                                  </>
                                )}
                              </button>
                              <p className="text-xs text-gray-500 mt-3 font-poppins">
                                Photo showing package with receiver is required before confirming delivery
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-green-50 rounded-volexx border-2 border-green-300">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                                <div>
                                  <p className="text-sm font-semibold text-green-800 font-poppins">
                                    Delivery Photo Uploaded
                                  </p>
                                  <p className="text-xs text-gray-600 font-poppins">
                                    Ready to enter OTP and complete delivery
                                  </p>
                                </div>
                              </div>
                              <img 
                                src={deliveryPhotos[order.id] || order.delivery_photo_url} 
                                alt="Delivery with receiver" 
                                className="h-16 w-16 object-cover rounded-volexx border-2 border-green-400"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Delivery Verification Checklist */}
                    {order.status === 'at_delivery' && (order.delivery_photo_url || deliveryPhotos[order.id]) && (
                      <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-volexx border-2 border-blue-300">
                        <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins flex items-center">
                          <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-2" />
                          Delivery Verification Checklist
                        </h4>
                        <p className="text-sm text-gray-600 mb-4 font-poppins">
                          Confirm all details match before completing delivery
                        </p>
                        
                        <div className="space-y-3">
                          {/* OTP Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-blue-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={deliveryChecklist[order.id]?.otpMatch || false}
                              onChange={() => toggleDeliveryChecklistItem(order.id, 'otpMatch')}
                              className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">OTP Code Matches</p>
                              <p className="text-xs text-gray-600 font-poppins">Receiver provided the correct OTP: {otps[order.id]}</p>
                            </div>
                          </label>

                          {/* Receiver Name Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-blue-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={deliveryChecklist[order.id]?.receiverNameMatch || false}
                              onChange={() => toggleDeliveryChecklistItem(order.id, 'receiverNameMatch')}
                              className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Receiver Name Verified</p>
                              <p className="text-xs text-gray-600 font-poppins">
                                Confirmed identity: {order.stops?.[1]?.contact_name || 'N/A'}
                              </p>
                            </div>
                          </label>

                          {/* Phone Number Match */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-blue-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={deliveryChecklist[order.id]?.phoneNumberMatch || false}
                              onChange={() => toggleDeliveryChecklistItem(order.id, 'phoneNumberMatch')}
                              className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Phone Number Verified</p>
                              <p className="text-xs text-gray-600 font-poppins">
                                Confirmed contact: {order.stops?.[1]?.contact_phone || 'N/A'}
                              </p>
                            </div>
                          </label>

                          {/* Package Condition */}
                          <label className="flex items-start p-3 bg-white rounded-volexx border-2 border-gray-200 hover:border-blue-400 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={deliveryChecklist[order.id]?.packageCondition || false}
                              onChange={() => toggleDeliveryChecklistItem(order.id, 'packageCondition')}
                              className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <p className="font-semibold text-gray-900 font-poppins">Package Condition Confirmed</p>
                              <p className="text-xs text-gray-600 font-poppins">Package delivered in good condition</p>
                            </div>
                          </label>
                        </div>

                        <button
                          onClick={() => handleCompleteDelivery(order.id)}
                          disabled={!isDeliveryChecklistComplete(order.id) || updatingStatus[order.id]}
                          className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-4 rounded-volexx transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-poppins flex items-center justify-center"
                        >
                          {updatingStatus[order.id] ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-6 w-6 mr-2" />
                              Mark as Delivered
                            </>
                          )}
                        </button>
                        
                        {!isDeliveryChecklistComplete(order.id) && (
                          <p className="text-xs text-center text-gray-500 mt-2 font-poppins">
                            ✓ Complete all verification checks to proceed
                          </p>
                        )}
                      </div>
                    )}

                    {/* Customer Rating Section - Show for completed deliveries */}
                    {order.status === 'delivered' && !ratedOrders[order.id] && (
                      <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-volexx border-2 border-yellow-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <StarIcon className="h-8 w-8 text-yellow-500 mr-3" />
                            <div>
                              <h4 className="font-bold text-gray-900 font-poppins">Rate Your Customer</h4>
                              <p className="text-sm text-gray-600 font-poppins">
                                Share your delivery experience with {order.customer_name}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setShowRatingModal(order.id)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-volexx transition-colors font-poppins"
                          >
                            Rate Customer
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Rating Submitted Confirmation */}
                    {order.status === 'delivered' && ratedOrders[order.id] && (
                      <div className="mb-6 p-4 bg-green-50 rounded-volexx border-2 border-green-300">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                          <div>
                            <h4 className="font-semibold text-green-900 font-poppins">Rating Submitted</h4>
                            <p className="text-sm text-green-700 font-poppins">Thank you for your feedback!</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      {nextAction && order.status !== 'delivered' && order.status !== 'at_delivery' && (
                        <button
                          onClick={() => handleStatusUpdate(order.id, nextAction.status)}
                          disabled={
                            isUpdating || 
                            (order.status === 'at_pickup' && !order.pickup_photo_url && !pickupPhotos[order.id])
                          }
                          className="flex-1 bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-3 px-4 rounded-volexx transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <span>{nextAction.label}</span>
                              <ArrowRightIcon className="h-5 w-5 ml-2" />
                            </>
                          )}
                        </button>
                      )}
                      
                      {nextAction && order.status === 'at_delivery' && !order.delivery_photo_url && !deliveryPhotos[order.id] && (
                        <button
                          onClick={() => handleStatusUpdate(order.id, nextAction.status)}
                          disabled={isUpdating}
                          className="flex-1 bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-3 px-4 rounded-volexx transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <span>{nextAction.label}</span>
                              <ArrowRightIcon className="h-5 w-5 ml-2" />
                            </>
                          )}
                        </button>
                      )}

                      <button
                        onClick={() => viewOrderDetails(order.id)}
                        className="bg-volexx-blue-600 hover:bg-volexx-blue-700 text-white font-semibold py-3 px-6 rounded-volexx transition-colors font-poppins"
                      >
                        View Details
                      </button>
                    </div>

                    {/* Package Details */}
                    {order.package_description && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-volexx">
                        <p className="text-xs font-semibold text-gray-700 mb-1 font-poppins">Package Details</p>
                        <p className="text-sm text-gray-900 font-poppins">{order.package_description}</p>
                        {order.package_weight_kg && (
                          <p className="text-xs text-gray-600 mt-1 font-poppins">Weight: {order.package_weight_kg} kg</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rating Modal */}
        {showRatingModal && orders.find(o => o.id === showRatingModal) && (
          <RiderRatingModal
            orderId={parseInt(showRatingModal)}
            customerName={orders.find(o => o.id === showRatingModal)?.customer_name || 'Customer'}
            customerId={orders.find(o => o.id === showRatingModal)?.customer_id || 0}
            onClose={() => setShowRatingModal(null)}
            onSuccess={() => {
              setShowRatingModal(null);
              setRatedOrders(prev => ({ ...prev, [showRatingModal]: true }));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MyJobs;
